import { Application, FeathersService, Query, Service } from '@feathersjs/feathers';
import { computed, makeObservable, reaction, runInAction, when, toJS } from "mobx";
import { DataType, IGroupUser, IItemUser, ISpaceUser, IUser, Language, Services, SpaceType, UserRole, UserStatus, UserType } from 'src/types';
import client from '../feathers';
import { User } from '../models';
import { computedFn } from 'mobx-utils';
import { Option } from "src/components";
import { BaseDataStore, RootStore } from '.';
import { DataContainer } from './dataContainer';
import i18next from 'i18next';
import toast from "react-hot-toast";
import { sortAlph } from 'src/util/util';

export class UserStore extends BaseDataStore<User, IUser>{

  protected readonly space_link_service: FeathersService<Application<ISpaceUser>, Service<ISpaceUser>> = client.service(Services.SpaceUsers);
  protected readonly user_link_service:  FeathersService<Application<IItemUser>, Service<IItemUser>> = client.service(Services.ItemUsers);

  links: DataContainer<IItemUser, IItemUser> = null;

  constructor(protected readonly rootStore: RootStore) {
    super(rootStore, User.create, DataType.User);

    makeObservable(this, {
      getLinks:       computed,
      getOnlineUsers: computed,
    });

    // Event Listeners
    this.space_link_service.on("created", (data: ISpaceUser) => {
      this.updateContainerEntry(Number(data.user_id), "spaces",
        { ...this.container[Number(data.user_id)]?.spaces || [], [data.space_id]: data }
      );

      // Update the active user links when a new space_user was created
      if(Number(data.user_id) === this.rootStore.authStore.getCurUserID) {
        this.container[Number(data.user_id)].room_id       =  this.rootStore.authStore.user.room_id;
        this.container[Number(data.user_id)].project_id    =  this.rootStore.authStore.user.project_id;
        this.container[Number(data.user_id)].workspace_id  =  this.rootStore.authStore.user.workspace_id;
        runInAction(() => this.rootStore.authStore.user = this.container[Number(data.user_id)]);
      }

      // If access to access to a new workspace was granted, load that space
      if(data.space_id === data.workspace_id) {
        this.rootStore.spaceStore.loadSpace(data.space_id);
      }
    });
  
    
    // TMP Note: I'm not 100% this is the way we handle patch on space_user level?
    this.space_link_service.on("patched", (data: ISpaceUser) => {
      if(this.container[Number(data.user_id)] && this.container[Number(data.user_id)].spaces[data.space_id])
        runInAction(() => {
            this.container[Number(data.user_id)].spaces[data.space_id].display_as = data.display_as;
        });
    });

    this.space_link_service.on("removed", (data: ISpaceUser) => {
      this.updateContainerEntry(Number(data.user_id), "spaces",
        { ...this.container[Number(data.user_id)].spaces, [data.space_id]: undefined }
      );

      // Update the active user links when a new space_user was created
      if(Number(data.user_id) === this.rootStore.authStore.getCurUserID) {
        this.container[Number(data.user_id)].room_id       =  this.rootStore.authStore.user.room_id;
        this.container[Number(data.user_id)].project_id    =  this.rootStore.authStore.user.project_id;
        this.container[Number(data.user_id)].workspace_id  =  this.rootStore.authStore.user.workspace_id;
        runInAction(() => this.rootStore.authStore.user    = this.container[Number(data.user_id)]);
      }

      // If access to access to a new workspace was revoked, unload that space
      if(data.space_id === data.workspace_id) {
        this.rootStore.spaceStore.loadSpace(data.space_id);
      }
    });
    client.service(Services.GroupUsers).on("created", (data: IGroupUser) => {
      this.updateContainerEntry(Number(data.user_id), "userGroups",
        [...this.container[Number(data.user_id)].userGroups, data.group_id]
      );
      this.updateContainerEntry(Number(data.group_id), "groupUsers",
        [...this.container[Number(data.group_id)].groupUsers, data.user_id]
      );
    });
    client.service(Services.GroupUsers).on("removed", (data: IGroupUser) => {
      this.updateContainerEntry(Number(data.user_id), "userGroups",
        this.container[Number(data.user_id)].userGroups.filter(g => Number(g) !== Number(data.group_id))
      );
      this.updateContainerEntry(Number(data.group_id), "groupUsers",
        this.container[Number(data.group_id)].groupUsers.filter(u => Number(u) !== Number(data.user_id))
      );
    });

  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init() {
    super.init();

    // Load initial data on login:
    when(() => this.rootStore.authStore.isLoggedIn, () => {
      this.loadItems(this.getCurFilter());
    }, {name: "user_login_load"});

    // Load users when changing workspace
    reaction(() => this.rootStore.authStore.getCurWorkspaceID, () => {
      if(this.rootStore.authStore.getCurWorkspaceID) {
        this.loadItems(this.getCurFilter())
      }
    }, {name: "user_workspace_load"});

    // Load user links when an item is set as active
    reaction(() => this.rootStore.getActiveItem && this.rootStore.getActiveItem.id, () => {
      if(this.rootStore.getActiveItem?.id) {
        this.links.setQuery(this.getLinkQuery);
        this.links.load();
      }
      else this.links.clear();
    }, {name: "user_onActive_loadLinks"});

    // Listen to active users for current room
    client.service(Services.Session).on('user_connected', (data: any) =>  {
      if(!this.rootStore.spaceStore.container[data.room_id]) return;

      if(this.rootStore.spaceStore.container[data.room_id].active_users.length == data.users.length) return;

      runInAction(() => {
        this.rootStore.spaceStore.container[data.room_id].active_users = data.users.map((u: IUser) => User.create(u));

        this.rootStore.spaceStore.container[data.room_id].active_users.map( u => {
          this.rootStore.userStore.container[u.id].setSpaceSetting(data.room_id, 'online', true, false) // set user to online in room
        });
      });

      const userJoined = this.rootStore.spaceStore.container[data.room_id].active_users.find(u => u.id == data.user_id);
      if(userJoined && userJoined.id != this.rootStore.authStore.getCurUserID && data.room_id == this.rootStore.authStore.getCurRoomID) {
        toast.success(`${userJoined.first_name} ${userJoined.last_name} entered the room`);
      }
      
    });

    client.service(Services.Session).on('user_disconnected', (data: any) =>  {
      if(this.rootStore.spaceStore.container[data.room_id]) {
        const userLeft = this.rootStore.spaceStore.container[data.room_id].active_users.find(u => u.id == data.user_id);
        this.rootStore.spaceStore.container[data.room_id].active_users = data.users.map((u: IUser) => User.create(u));
        if(userLeft && userLeft.id != this.rootStore.authStore.getCurUserID && data.room_id == this.rootStore.authStore.getCurRoomID) {
          this.rootStore.userStore.container[userLeft.id].setSpaceSetting(data.room_id, 'online', false, false) // set user to online in room
          toast.success(`${userLeft.first_name} ${userLeft.last_name} left the room`);
        }
      }
    });
    
    this.setupStorage();
  }
  protected getCurFilter = (): Query => ({});

  clear() {
    super.clear();
  }

  /**
   * Called by updated and patched event listener. Checks if the updated 
   * item is set as active or current user.
   */
  protected updateActive(data: IUser): void {
    super.updateActive(data);
    if(Number(data.id) === this.rootStore.authStore.getCurUserID) this.rootStore.authStore.user.patch(data);
  }

  /*******************************************************************************************************************************************
   * Database access and Container management
   ********************************************************************************************************************************************/
  public static parseItemUserLink = (link: IItemUser) => {
    return {
      ...link,
      item_id:      Number(link.item_id),
      room_id:      Number(link.room_id),
      user_id:      Number(link.user_id),
      workspace_id: Number(link.workspace_id),
    }
  }
  protected setupStorage = () => {
    this.links = new DataContainer<IItemUser, IItemUser>(
          this.getLinkQuery, 
          this.user_link_service,
          { // Config
            paginate:    false,
            limited:     false,
            sortProp:    "id",
            eventFilter: (data: IItemUser) => Number(data.item_id) === this.rootStore.getActiveItem?.id,
            conversion:  UserStore.parseItemUserLink,
            name: this.data_type + ".links",
          }
      );
    this.user_link_service.on("created", (data: IItemUser) => this.links.update(data, "created"));
    this.user_link_service.on("removed", (data: IItemUser) => this.links.update(data, "removed"));
    this.user_link_service.on("patched", (data: IItemUser) => this.links.update(data, "updated"));
    this.user_link_service.on("updated", (data: IItemUser) => this.links.update(data, "updated"));
    reaction(() => this.rootStore.getActiveItem, () => this.links.setQuery(this.getLinkQuery));
  }

  protected get getLinkQuery() {
    return this.rootStore.getActiveItem ? {
      item_id: this.rootStore.getActiveItem.id,
    } : null;
  }

  /**
   * Adds a user link to user @user_id with type @link_type to the current active item.
   */
  addLink = async (user_id: number, link_type: string) => {
    if(this.links.getArray.find(l => l.user_id === user_id) || !this.rootStore.getActiveItem) return;

    const link: IItemUser = {
      user_id:      user_id,
      item_id:      this.rootStore.getActiveItem.id,
      data_type:    this.rootStore.getActiveItem.data_type,
      room_id:      this.rootStore.getActiveItem.room_id,
      workspace_id: this.rootStore.getActiveItem.workspace_id,
      link_type:    link_type,
    }
    try {
      await this.user_link_service.create(link);
    } catch (err) {
      console.error("Failed to create link", err);
    }
  }

  /**
   * Removes all user links to user @user_id with type @link_type from the current active item.
   */
  removeLink = async (user_id: number, type: string) => {
    const link = this.links.getArray.find(l => l.user_id === user_id);
    if(!link) return;
    try {
      await this.user_link_service.remove(link.id);
    } catch (err) {
      console.error("Failed to remove link", err);
    }
  }

  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/
  get getLinks() {
    return this.links.getArray.map(l => this.container[l.user_id]);
  }

  getMentions = computedFn((space_id: number): User[] => {
    return sortAlph(this.getItems.filter(u => u.inSpace(space_id)), u => u.getFullName.toLowerCase());
  });

  getLinkOptions = computedFn((type: UserType[] | "all"): Option[] => {
    const item = this.rootStore.getActiveItem;
    if(!item) return [];
    return this.getUsersBySpace(item.room_id, type)
      .filter(Boolean)
      .filter(u => !u.getCurSpaceSettings?.hide_in_list)
      .map(u => ({value: u, label: u.displayName}));
  });
  
  getUsersBySpace = computedFn((space_id: number, type: UserType[] | "all"): User[] => {
    const users = this.getItems.filter(u => Boolean(u.spaces && u.spaces[space_id]));
    return sortAlph(
      (!type || type === "all") ? users : users.filter(u => type.includes(u.type))
    , u => u.getFullName.toLowerCase());
  });

  /**
   * @return list of users that are online and inside the current room of the current user
   */
  get getOnlineUsers(): User[] {
    if(!this.rootStore.authStore.getCurRoomID) return [];
    if(!this.rootStore.spaceStore.container[this.rootStore.authStore.getCurRoomID]) return [];
    return sortAlph(
      this.rootStore.spaceStore.container[this.rootStore.authStore.getCurRoomID].active_users.slice()
    , u => u.getFullName.toLowerCase());
  }

  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/
  

  /*******************************************************************************************************************************************
   * Actions 
   ********************************************************************************************************************************************/
  createGroup = async (name: string, space_id: number) => {
    const group = User.create({
      first_name:   name,
      workspace_id: this.rootStore.authStore.getCurWorkspaceID,
      type:         UserType.Group,
      email:        "",
      groupUsers:   [],
      userGroups:   [],
      language:     Language.EN,
    });
    try {
      await group.save();
      await group.linkSpace(this.rootStore.authStore.getCurWorkspaceID, SpaceType.Workspace, UserRole.Member, UserStatus.Accepted);
      if(space_id !== this.rootStore.authStore.getCurWorkspaceID) {
        const type: SpaceType = this.rootStore.spaceStore.container[space_id].type;
        if(!type) {
          console.error("Failed to get space type for id. Only workspace user was created.", space_id);
          return;
        }
        await group.linkSpace(space_id, type, UserRole.Member, UserStatus.Accepted)
      };
    } catch (err) {
      console.error("Failed to create group", err);
      throw err;
    }
  }
}